/*
╔══════════════════════════════════════════════════════════════════════════╗
║  CART DRAWER - WEB COMPONENT CUSTOM ELEMENT                              ║
╚══════════════════════════════════════════════════════════════════════════╝

Questo file definisce un Custom Element HTML chiamato <cart-drawer> che gestisce
il drawer laterale del carrello.

WEB COMPONENT: 
- Estende HTMLElement per creare un elemento personalizzato
- Si registra come <cart-drawer> tramite customElements.define()
- Può essere usato nel markup HTML come un normale tag

INTERAZIONE CON YOMI-HEADER:
- Il file yomi-header.liquid contiene la funzione openCartDrawer()
- Quando l'utente clicca sull'icona carrello:
  1. openCartDrawer() aggiunge classe 'active' all'elemento <cart-drawer>
  2. Chiama il metodo open() di questa classe
  3. Il drawer si anima e appare lateralmente

*/

class CartDrawer extends HTMLElement {
  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║  CONSTRUCTOR - INIZIALIZZAZIONE DEL COMPONENTE                     ║
   * ╚════════════════════════════════════════════════════════════════════╝
   * 
   * Viene chiamato automaticamente quando l'elemento <cart-drawer> viene
   * creato nel DOM.
   * 
   * SETUP LISTENERS:
   * 1. Escape key → Chiude il drawer
   * 2. Click su overlay → Chiude il drawer  
   * 3. Click/keyboard su icona carrello → Apre il drawer
   */
  constructor() {
    super();

    // LISTENER 1: Tasto ESC per chiudere il drawer
    // - Ascolta l'evento 'keyup' su tutto l'elemento
    // - Se il tasto premuto è 'Escape', chiama this.close()
    // - Questo è uno standard per accessibilità (ARIA best practices)
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    // LISTENER 2: Click sull'overlay scuro di sfondo
    // - L'overlay (#CartDrawer-Overlay) è il div semitrasparente dietro il drawer
    // - Quando l'utente clicca fuori dal drawer (sull'overlay), si chiude
    // - .bind(this) mantiene il contesto della classe
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    
    // LISTENER 3: Setup dell'icona carrello nell'header
    // Questa funzione aggiunge i listener all'icona carrello per aprire il drawer
    this.setHeaderCartIconAccessibility();
  }

  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║  setHeaderCartIconAccessibility()                                  ║
   * ║  Configura listener e attributi ARIA sull'icona carrello header   ║
   * ╚════════════════════════════════════════════════════════════════════╝
   * 
   * Questa funzione trova l'icona del carrello nell'header e configura:
   * - Attributi ARIA per accessibilità screen reader
   * - Event listener per click del mouse
   * - Event listener per tastiera (SPACE key)
   * 
   * NOTA: Questa funzione cerca '#cart-link' che potrebbe essere l'icona
   * Shopify standard. Nel custom header YOMI, l'onclick inline su
   * cart-icon-bubble gestisce già l'apertura, ma questo fornisce un
   * fallback compatibile.
   */
  setHeaderCartIconAccessibility() {
    // Cerca l'elemento dell'icona carrello nell'header
    const cartLink = document.querySelector('#cart-link');
    if (!cartLink) return; // custom header has no #cart-link; its own onclick opens the drawer
    
    // Imposta attributi ARIA per accessibilità
    // role="button": Comunica agli screen reader che è cliccabile
    cartLink.setAttribute('role', 'button');
    
    // aria-haspopup="dialog": Indica che aprirà un dialog/drawer
    cartLink.setAttribute('aria-haspopup', 'dialog');
    
    // LISTENER CLICK: Gestisce il click del mouse
    cartLink.addEventListener('click', (event) => {
      // Previene navigazione se è un link <a href="...">
      event.preventDefault();
      
      // Apre il drawer passando l'elemento che ha triggerato l'azione
      // (importante per ripristinare il focus dopo la chiusura)
      this.open(cartLink)
    });
    
    // LISTENER TASTIERA: Gestisce la pressione della barra spaziatrice
    // (standard accessibilità per attivare elementi interattivi)
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║  open(triggeredBy)                                                 ║
   * ║  METODO PRINCIPALE: Apre il drawer laterale del carrello          ║
   * ╚════════════════════════════════════════════════════════════════════╝
   * 
   * Questo metodo viene chiamato da:
   * - openCartDrawer() in yomi-header.liquid
   * - setHeaderCartIconAccessibility() (listener icona standard)
   * - Altri punti del codice quando serve aprire il carrello
   * 
   * PARAMETRI:
   * @param {HTMLElement} triggeredBy - Elemento che ha triggerato l'apertura
   *                                     (serve per ripristinare focus dopo)
   * 
   * FLUSSO DI ESECUZIONE:
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ 1. Salva riferimento all'elemento trigger                        │
   * │ 2. Setup accessibilità per note/details nel drawer              │
   * │ 3. Nasconde widget esterni (es. wishlist Swym)                  │
   * │ 4. Aggiunge classi CSS 'animate' + 'active'                     │
   * │    → Trigger animazioni CSS di apertura                         │
   * │ 5. Aspetta fine transizione CSS                                 │
   * │ 6. Attiva trap focus per accessibilità                          │
   * │ 7. Blocca scroll del body                                       │
   * └──────────────────────────────────────────────────────────────────┘
   */
  open(triggeredBy) {
    
    // STEP 1: Salva l'elemento che ha triggerato l'apertura
    // Servirà per ripristinare il focus quando il drawer si chiude
    if (triggeredBy) this.setActiveElement(triggeredBy);
    
    // STEP 2: Gestione accessibilità per eventuali <details> nel drawer
    // (es. sezione note o istruzioni speciali)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
    
    // STEP 3: Nascondi widget wishlist Swym durante l'apertura
    // (evita sovrapposizioni visive o conflitti z-index)
    document.querySelectorAll('.swym-wishlist-collections-v2-container').forEach(el => {
      el.style.display = 'none';
    });
    
    // STEP 4: Animazione di apertura
    // setTimeout garantisce che il browser faccia un repaint prima di applicare
    // le classi, assicurando che l'animazione CSS venga triggerata correttamente
    setTimeout(() => {
      // 'animate': Trigger per le animazioni CSS
      // 'active': Rende visibile il drawer (display/opacity)
      this.classList.add('animate', 'active')
    });

    // STEP 5: Quando la transizione CSS termina...
    this.addEventListener('transitionend', () => {
      // Determina quale contenitore usare per il trap focus
      // - Se carrello vuoto: usa .drawer__inner-empty
      // - Se carrello pieno: usa #CartDrawer
      const containerToTrapFocusOn = this.classList.contains('is-empty') 
        ? this.querySelector('.drawer__inner-empty') 
        : document.getElementById('CartDrawer');
      
      // Elemento che riceverà il focus iniziale
      const focusElement = this.querySelector('.drawer__inner') 
        || this.querySelector('.drawer__close');
      
      // TRAP FOCUS: Impedisce che Tab/Shift+Tab escano dal drawer
      // (importante per accessibilità e usabilità keyboard)
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true }); // once:true → listener si rimuove dopo prima esecuzione

    // STEP 6: Blocca scroll della pagina sottostante
    // Quando il drawer è aperto, solo il drawer può scrollare
    document.body.classList.add('overflow-hidden');
  }

  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║  close()                                                           ║
   * ║  Chiude il drawer laterale del carrello                           ║
   * ╚════════════════════════════════════════════════════════════════════╝
   * 
   * Chiamato da:
   * - Listener ESC key nel constructor
   * - Click sull'overlay di sfondo
   * - Bottone X di chiusura nel drawer
   * - Dopo completamento checkout (opzionale)
   * 
   * OPERAZIONI:
   * 1. Rimuove classe 'active' → Trigger animazione di chiusura CSS
   * 2. Ripristina display della wishlist Swym
   * 3. Rilascia il trap focus
   * 4. Ripristina scroll del body
   */
  close() {
    // STEP 1: Rimuove classe 'active' 
    // Il CSS usa questa classe per controllare visibilità e animazioni
    // Rimuoverla triggera l'animazione di slide-out
    this.classList.remove('active');
    
    // STEP 2: Ri-mostra la wishlist Swym (nascosta durante open())
    document.querySelectorAll('.swym-wishlist-collections-v2-container').forEach(el => {
      el.style.display = ''; // Ripristina display originale
    });
    
    // STEP 3: Rimuove il trap focus e ripristina focus sull'elemento trigger
    // this.activeElement è stato salvato in open() e contiene l'icona carrello
    removeTrapFocus(this.activeElement);
    
    // STEP 4: Riabilita scroll della pagina principale
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║  setActiveElement(element)                                         ║
   * ║  Salva riferimento all'elemento che ha aperto il drawer           ║
   * ╚════════════════════════════════════════════════════════════════════╝
   * 
   * Memorizza l'elemento HTML che ha triggerato l'apertura del drawer.
   * Questo riferimento viene usato da close() per ripristinare il focus
   * sull'icona carrello dopo la chiusura (migliora accessibilità).
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}

/*
╔══════════════════════════════════════════════════════════════════════════╗
║  REGISTRAZIONE WEB COMPONENT                                             ║
╚══════════════════════════════════════════════════════════════════════════╝

Registra la classe CartDrawer come Custom Element HTML con tag <cart-drawer>.

Dopo questa riga:
- Puoi usare <cart-drawer> nel markup HTML/Liquid
- Il browser crea automaticamente un'istanza di CartDrawer
- Tutti i metodi e listener sono disponibili

ESEMPIO USO NEL MARKUP:
<cart-drawer>
  <div id="CartDrawer-Overlay"></div>
  <div class="drawer__inner">
    <!-- Contenuto carrello -->
  </div>
</cart-drawer>

*/
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
